import {
  i18n,
  useTranslation,
  type Resources,
} from '@bofrak-backend/shared-i18n';
import en from './locales/en/shared-ui.json';
import fr from './locales/fr/shared-ui.json';
import ptPT from './locales/pt-PT/shared-ui.json';

/**
 * i18next namespace for shared-ui strings. Keys are English (English-as-key);
 * fr/pt-PT live next to this file and are checked by `pnpm i18n:check`.
 */
export const SHARED_UI_NS = 'shared-ui';

/** Merge into an app's resources: `mergeResources(app, sharedUiResources)`. */
export const sharedUiResources: Resources = {
  en: { [SHARED_UI_NS]: en },
  fr: { [SHARED_UI_NS]: fr },
  'pt-PT': { [SHARED_UI_NS]: ptPT },
};

type TOptions = Record<string, unknown>;
export type SharedUiT = (key: string, options?: TOptions) => string;

// Apps that never call `initI18n` (e.g. web-app-admin) still render shared-ui
// in English: read the en file (plural forms included) and interpolate.
const englishT: SharedUiT = (key, options) => {
  const count = options?.count;
  const pluralKey =
    typeof count === 'number' ? `${key}_${count === 1 ? 'one' : 'other'}` : '';
  const text =
    (en as Record<string, string>)[pluralKey] ??
    (en as Record<string, string>)[key] ??
    key;
  return text.replace(/{{\s*(\w+)\s*}}/g, (_, name: string) =>
    String(options?.[name] ?? ''),
  );
};

/** Non-React translation for shared-ui (toasts in utils, constants). */
export const tShared: SharedUiT = (key, options) =>
  i18n.isInitialized
    ? i18n.t(key, { ...options, ns: SHARED_UI_NS })
    : englishT(key, options);

/** React hook for shared-ui components; re-renders on language change. */
export const useSharedUiTranslation = (): { t: SharedUiT } => {
  const { t, ready } = useTranslation(SHARED_UI_NS, {
    i18n,
    useSuspense: false,
  });
  return { t: ready ? (t as unknown as SharedUiT) : englishT };
};
